import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getTerm } from './terms';

// ────────────────────────────────────────────────────────────────────────────
// GlossaryProvider — app-wide state for "which term is being explained right
// now?" Lets any component anywhere in the tree pop the panel via
// `useGlossary().open('term_id')`.
//
// History stack lets the user navigate "back to the previous term" when they
// clicked a chip in the related list. We keep a hard cap (8) so the stack
// doesn't grow forever; back-history is a UX nicety, not durable state.
// ────────────────────────────────────────────────────────────────────────────

interface GlossaryState {
  /** The term id currently shown in the panel, or null when closed. */
  currentId: string | null;
  /** Back-history stack (most recent at top). Excludes the current term. */
  history: string[];
}

interface GlossaryApi {
  state: GlossaryState;
  /** Open the panel pinned to a given term id. Pushes the previously-open
   *  term onto the history stack. Unknown ids are ignored (with a dev warn). */
  open: (id: string) => void;
  /** Switch the panel to a new term, pushing the current onto history. */
  switchTo: (id: string) => void;
  /** Pop the most recent history entry and show it. */
  back: () => void;
  /** Close the panel. Does NOT clear history (so re-open + back works). */
  close: () => void;
}

const GlossaryContext = createContext<GlossaryApi | null>(null);

const HISTORY_CAP = 8;

interface ProviderProps {
  children: ReactNode;
}

export function GlossaryProvider({ children }: ProviderProps) {
  const [state, setState] = useState<GlossaryState>({
    currentId: null,
    history: [],
  });

  // Stable ref so callbacks always see the latest state without retriggering
  // child useEffects via dependency churn.
  const stateRef = useRef(state);
  stateRef.current = state;

  const open = useCallback((id: string) => {
    if (!getTerm(id)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[glossary] open: unknown term id "${id}"`);
      }
      return;
    }
    const prev = stateRef.current.currentId;
    setState(s => ({
      currentId: id,
      // If user re-opens the same term, don't pollute history with itself.
      history:
        prev && prev !== id
          ? [prev, ...s.history].slice(0, HISTORY_CAP)
          : s.history,
    }));
  }, []);

  const switchTo = useCallback((id: string) => {
    if (!getTerm(id)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[glossary] switchTo: unknown term id "${id}"`);
      }
      return;
    }
    const prev = stateRef.current.currentId;
    setState(s => ({
      currentId: id,
      history:
        prev && prev !== id
          ? [prev, ...s.history].slice(0, HISTORY_CAP)
          : s.history,
    }));
  }, []);

  const back = useCallback(() => {
    setState(s => {
      if (s.history.length === 0) return s;
      const [head, ...rest] = s.history;
      return { currentId: head, history: rest };
    });
  }, []);

  const close = useCallback(() => {
    setState(s => ({ currentId: null, history: s.history }));
  }, []);

  // Esc to close — easy keyboard escape hatch from anywhere.
  useEffect(() => {
    if (state.currentId == null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.currentId, close]);

  const api: GlossaryApi = useMemo(
    () => ({ state, open, switchTo, back, close }),
    [state, open, switchTo, back, close],
  );

  return (
    <GlossaryContext.Provider value={api}>{children}</GlossaryContext.Provider>
  );
}

export function useGlossary(): GlossaryApi {
  const ctx = useContext(GlossaryContext);
  if (!ctx) {
    throw new Error(
      'useGlossary must be called inside <GlossaryProvider>. Wrap your app at the root.',
    );
  }
  return ctx;
}
